import Navbar from '../../../components/feature/Navbar';
import Footer from '../../../components/feature/Footer';
import PageHero from '../../../components/sections/PageHero';
import PageTrustBar from '../../../components/sections/PageTrustBar';
import PageCollections from '../../../components/sections/PageCollections';
import PageAIAnswer from '../../../components/sections/PageAIAnswer';
import PageProcess from '../../../components/sections/PageProcess';
import PageWhyUs from '../../../components/sections/PageWhyUs';
import PageServiceArea from '../../../components/sections/PageServiceArea';
import PageTestimonials from '../../../components/sections/PageTestimonials';
import PageFAQ from '../../../components/sections/PageFAQ';
import PageFinalCTA from '../../../components/sections/PageFinalCTA';
import {
  FORM_URL, MAP_EMBED, SERVICE_AREAS, SERVICE_ADDRESS, SERVICE_HOURS, SERVICE_PHONE, TESTIMONIALS,
} from '../../../mocks/siteData';

const trustItems = [
  { icon: 'ri-gift-line', label: 'Thoughtful Gifts' },
  { icon: 'ri-home-heart-line', label: 'Unique Home Décor' },
  { icon: 'ri-sparkling-line', label: 'Curated Selection' },
  { icon: 'ri-refresh-line', label: 'New Arrivals Weekly' },
  { icon: 'ri-heart-line', label: 'Something for Everyone' },
];

const subcategories = [
  {
    title: 'Artisan Gifts & Keepsakes',
    description: 'Candles, soaps, ceramics, small sculptures, and handcrafted items perfect for any occasion.',
    href: '/collections/gifts',
    tag: 'Best Sellers',
    imageSrc: 'https://public.readdy.ai/ai/img_res/fc91fbebc9a39844520f6adaf5bd8d83.jpg',
  },
  {
    title: 'Home Décor & Accents',
    description: 'Vases, trays, picture frames, decorative boxes, and accent pieces to elevate any interior.',
    href: '/collections/gifts',
    tag: 'Featured',
    imageSrc: 'https://readdy.ai/api/search-image?query=home%20decor%20accent%20pieces%20decorative%20vases%20wooden%20trays%20picture%20frames%20candle%20holders%20arranged%20beautifully%20warm%20editorial%20photography%20boutique%20interior%20design%20shop&width=800&height=600&seq=ye-gifts-decor&orientation=landscape',
  },
  {
    title: 'Vintage-Inspired Décor',
    description: 'Antique-style prints, vintage signs, nostalgic objects, and décor with old-world character.',
    href: '/collections/gifts',
    tag: 'Popular',
    imageSrc: 'https://readdy.ai/api/search-image?query=vintage%20inspired%20home%20decor%20antique%20prints%20old%20signs%20nostalgic%20objects%20rustic%20frames%20warm%20photography%20editorial%20boutique%20style%20cozy%20interior%20design&width=800&height=600&seq=ye-gifts-vintage&orientation=landscape',
  },
];

const aiAnswers = [
  {
    q: 'What kinds of gifts does Yarnell Emporium carry?',
    a: 'Yarnell Emporium offers artisan candles, handcrafted ceramics, decorative home accents, vintage-inspired items, and unique keepsakes — all curated for quality and uniqueness. Perfect for birthdays, holidays, housewarmings, and special occasions.',
  },
  {
    q: 'Can I find unique home décor at Yarnell Emporium?',
    a: 'Yes — the home décor collection includes vases, trays, frames, candle holders, prints, and accent pieces. Items are chosen for their distinct character and quality, making them excellent alternatives to generic chain store décor.',
  },
  {
    q: 'Does Yarnell Emporium carry gifts for all budgets?',
    a: 'The gift selection spans a range of price points, from small artisan items to more significant decorative pieces. There\'s something thoughtful and unique for every budget, making it an ideal destination for finding gifts that stand out.',
  },
  {
    q: 'Are the home décor items at Yarnell Emporium new or antique?',
    a: 'The collection includes both new artisan-made items and vintage/antique décor pieces. Many items have a vintage-inspired or antique aesthetic while being perfectly suited for modern homes, offering the best of both worlds.',
  },
];

const processSteps = [
  { number: '01', title: 'Browse with Intention', description: 'Think about the occasion, recipient, or space you\'re shopping for. Our gift selection is curated to make that decision easier.' },
  { number: '02', title: 'Discover the Perfect Piece', description: 'From small artisan gifts to statement home accents, explore items you genuinely won\'t find at any other store.' },
  { number: '03', title: 'Get Styling Ideas', description: 'Not sure how a décor piece will look in your home? We\'re happy to offer ideas on styling and placement.' },
  { number: '04', title: 'Give Something Unforgettable', description: 'Walk away with a gift or décor piece that\'s as unique as the person you\'re buying for — or your own beautiful home.' },
];

const whyUsItems = [
  { icon: 'ri-gift-2-line', title: 'Gifts That Stand Out', description: 'Every item in our gift collection is chosen because it\'s genuinely different — no generic mall gifts here.' },
  { icon: 'ri-sparkling-2-line', title: 'Curated for Quality', description: 'From artisan candles to vintage home accents, quality and character are the standard for everything we carry.' },
  { icon: 'ri-refresh-line', title: 'Fresh Arrivals Constantly', description: 'New gift items arrive regularly, making Yarnell Emporium a go-to source for seasonal and special-occasion gifting.' },
  { icon: 'ri-home-smile-line', title: 'Perfect for Home Decorators', description: 'Our décor items are ideal for those who want a distinctive, curated interior that doesn\'t look mass-produced.' },
  { icon: 'ri-user-heart-line', title: 'Personal Recommendations', description: 'Tell us what you\'re looking for and we\'ll help you find the perfect gift or accent piece.' },
  { icon: 'ri-price-tag-line', title: 'Options for Every Budget', description: 'Thoughtful, unique gifts at a range of price points — from affordable artisan finds to meaningful statement pieces.' },
];

const faqItems = [
  { q: 'Where can I find unique gifts near Yarnell or Wickenburg, AZ?', a: 'Yarnell Emporium offers a wide selection of unique gifts, artisan items, and home décor in Yarnell, AZ. It\'s a top destination for shoppers looking for distinctive, non-generic gifts for any occasion near Wickenburg, Prescott, and the greater Phoenix area.' },
  { q: 'Do you have gifts for home decoration?', a: 'Yes — Yarnell Emporium carries a strong selection of home décor gifts including vases, candle holders, decorative trays, framed prints, and vintage-inspired accents. Items are curated for homes that value character and individuality.' },
  { q: 'Can I find last-minute gifts at Yarnell Emporium?', a: 'Absolutely. The store is open every day from 9am–5pm, making it a great spot for thoughtful last-minute gift shopping. The curated selection ensures you\'ll find something genuinely special rather than a generic fallback gift.' },
  { q: 'Do you gift wrap or package gifts at the store?', a: 'Please call us at 928.427-9593 to ask about gift packaging options. We can discuss what\'s available for your specific purchase when you visit the store.' },
  { q: 'How often does the gift selection change at Yarnell Emporium?', a: 'New gift items arrive regularly — often weekly. The rotating selection means there\'s always something fresh and new, making each visit feel like a new discovery. Perfect for repeat shoppers looking for seasonal gifting options.' },
];

export default function GiftsPage() {
  return (
    <div className="font-sans">
      <Navbar />
      <main>
        <PageHero
          badge="Gifts & Home Décor · Yarnell, AZ"
          headline="Curated Gifts &"
          headlineItalic="Home Décor"
          subheadline="Artisan gifts, distinctive home accents, and one-of-a-kind décor pieces — thoughtfully curated for every occasion and every space."
          urgency="New gift arrivals added weekly"
          ctas={[
            { label: 'Visit the Store', href: 'https://maps.google.com/?q=22899+S+Hwy+89+Yarnell+AZ+85362', external: true, primary: true },
            { label: 'Get Directions', href: 'https://maps.google.com/?q=22899+S+Hwy+89+Yarnell+AZ+85362', external: true },
          ]}
          imageSrc="https://readdy.ai/api/search-image?query=elegant%20boutique%20gift%20shop%20interior%20artisan%20candles%20decorative%20ceramics%20vases%20curated%20display%20warm%20amber%20lighting%20cinematic%20editorial%20photography%20high%20end%20retail%20curated%20gifts&width=1920&height=1080&seq=ye-hero-gifts&orientation=landscape"
        />
        <PageTrustBar items={trustItems} />
        <PageCollections
          heading="Gifts & Décor Collections"
          subheading="Something for Everyone"
          intro="From artisan keepsakes to statement home accents — every item in our gift collection was chosen because it truly stands out."
          items={subcategories}
        />
        <PageAIAnswer
          heading="Gifts at Yarnell Emporium"
          subheading="What We Offer"
          items={aiAnswers}
        />
        <PageProcess
          heading="Finding the Perfect Gift"
          subheading="How It Works"
          intro="Great gifts come from great curation. Here\'s how to make the most of your Yarnell Emporium gift shopping experience."
          steps={processSteps}
        />
        <PageWhyUs
          heading="Why Shop Gifts Here"
          subheading="Our Promise"
          intro="We curate our gift collection with the same passion we bring to antiques. Every item is genuinely special."
          items={whyUsItems}
          imageSrc="https://readdy.ai/api/search-image?query=boutique%20gift%20shop%20display%20warm%20golden%20light%20artisan%20items%20candles%20ceramics%20carefully%20arranged%20shelves%20intimate%20editorial%20photography&width=600&height=800&seq=ye-why-gifts&orientation=portrait"
        />
        <PageServiceArea
          heading="Unique Gifts in Yarnell, AZ"
          subheading="Serving the Region"
          intro="Shoppers come from Prescott, Wickenburg, Phoenix, and across Arizona to find gifts they can\'t find anywhere else."
          areas={SERVICE_AREAS}
          address={SERVICE_ADDRESS}
          hours={SERVICE_HOURS}
          phone={SERVICE_PHONE}
          mapEmbed={MAP_EMBED}
        />
        <PageTestimonials
          heading="Customers Love Our Gifts"
          subheading="Reviews"
          items={TESTIMONIALS}
        />
        <PageFAQ
          heading="Gift Shopping Questions"
          subheading="FAQ"
          items={faqItems}
        />
        <PageFinalCTA
          heading="Give a Gift They'll"
          headingItalic="Actually Remember"
          subheading="Browse our curated gift and home décor collection at Yarnell Emporium. Open daily 9am–5pm in Yarnell, AZ."
          urgency="Fresh gift arrivals every week"
          formUrl={FORM_URL}
          imageSrc="https://readdy.ai/api/search-image?query=gift%20boutique%20shop%20warm%20intimate%20lighting%20artisan%20gifts%20displayed%20beautifully%20editorial%20photography%20high%20end%20retail%20experience%20curated%20shop&width=1920&height=1080&seq=ye-cta-gifts&orientation=landscape"
        />
      </main>
      <Footer />
    </div>
  );
}